/**
 * Phase 107 (Plan 107-07 / SLA-PRICE-03, SLA-PRICE-08) — frontend API client
 * for the SLA pricing configurator.
 *
 * Follows `leads.ts`'s shape exactly: plain exported `interface` DTOs, thin
 * one-line wrapper functions over `apiGet`/`apiPost`/`apiPatch`/`apiPut`/
 * `apiDelete`, no fetch logic beyond that.
 *
 * DTOs mirror `backend/src/api/schemas/slaPricing.ts` and
 * `backend/src/services/slaPricingConfigurationService.ts` field for field.
 * Money fields (`hourlyRate`, `monthlyPrice`, `monthlyAmount`) are typed as
 * `string | null` / `string` — never `number` — because the backend emits
 * fixed-2 decimal strings so no float representation ever reaches the
 * browser (T-107-MONEYWIRE).
 *
 * D-04/D-14: no rate field or currency field on `SlaPricingCell` — the rate
 * lives on the band, the currency lives on the configuration.
 * D-05/D-11: no team, member, project, customer or owner type anywhere in this
 * module. MKS-346 added the one exception — `SlaPricingLinkedLead` (id + name)
 * and `leadCount`, so the configurator can answer "which deals is this priced
 * for?" and the delete confirmation can say how many links it will drop.
 */
import { apiGet, apiPost, apiPatch, apiPut, apiDelete } from './client';

export interface SlaPricingBand {
  id: number;
  configurationId: number;
  name: string;
  abbreviation: string;
  color: string;
  hourlyRate: string | null;
}

export interface SlaPricingCell {
  dayOfWeek: number;
  hour: number;
  bandId: number;
}

export interface SlaPricingBandBreakdown {
  bandId: number;
  name: string;
  color: string;
  abbreviation: string;
  hoursPerWeek: number;
  hourlyRate: string | null;
  monthlyAmount: string;
  hasRate: boolean;
}

/** MKS-346 — one lead that links a configuration. Id + name only. */
export interface SlaPricingLinkedLead {
  id: number;
  name: string;
}

export interface SlaPricingConfigurationSummary {
  id: number;
  name: string;
  currency: string;
  baseTimezone: string;
  bandCount: number;
  cellCount: number;
  monthlyPrice: string;
  // MKS-346 — how many leads link this configuration, so the list page's
  // delete confirmation can say how many links it is about to drop.
  leadCount: number;
  updatedAt: string;
}

export interface SlaPricingConfigurationDetail extends SlaPricingConfigurationSummary {
  bands: SlaPricingBand[];
  cells: SlaPricingCell[];
  breakdown: SlaPricingBandBreakdown[];
  // MKS-346 — the leads this configuration was priced for.
  leads: SlaPricingLinkedLead[];
}

export interface CreateConfigurationInput {
  name: string;
  currency: string;
  baseTimezone?: string;
}

export interface UpdateConfigurationInput {
  name?: string;
  currency?: string;
  baseTimezone?: string;
}

export interface CreateBandInput {
  name: string;
  abbreviation: string;
  color: string;
  hourlyRate?: string | null;
}

export interface UpdateBandInput {
  name?: string;
  abbreviation?: string;
  color?: string;
  hourlyRate?: string | null;
}

export interface SaveCellsInput {
  cells: SlaPricingCell[];
}

export async function fetchConfigurations(): Promise<{ data: SlaPricingConfigurationSummary[] }> {
  return apiGet('/sla-pricing/configurations');
}

export async function fetchConfiguration(
  id: number,
): Promise<{ data: SlaPricingConfigurationDetail }> {
  return apiGet(`/sla-pricing/configurations/${id}`);
}

export async function createConfiguration(
  input: CreateConfigurationInput,
): Promise<{ data: SlaPricingConfigurationDetail }> {
  return apiPost('/sla-pricing/configurations', input);
}

export async function updateConfiguration(
  id: number,
  input: UpdateConfigurationInput,
): Promise<{ data: SlaPricingConfigurationDetail }> {
  return apiPatch(`/sla-pricing/configurations/${id}`, input);
}

export async function deleteConfiguration(id: number): Promise<{ data: { success: boolean } }> {
  return apiDelete(`/sla-pricing/configurations/${id}`);
}

export async function duplicateConfiguration(
  id: number,
): Promise<{ data: SlaPricingConfigurationDetail }> {
  return apiPost(`/sla-pricing/configurations/${id}/duplicate`);
}

// Band paths are nested under the configuration, matching the backend, so
// the configurationId is always on the wire.
export async function createBand(
  configurationId: number,
  input: CreateBandInput,
): Promise<{ data: SlaPricingBand }> {
  return apiPost(`/sla-pricing/configurations/${configurationId}/bands`, input);
}

export async function updateBand(
  configurationId: number,
  bandId: number,
  input: UpdateBandInput,
): Promise<{ data: SlaPricingBand }> {
  return apiPatch(`/sla-pricing/configurations/${configurationId}/bands/${bandId}`, input);
}

export async function deleteBand(
  configurationId: number,
  bandId: number,
): Promise<{ data: { success: boolean } }> {
  return apiDelete(`/sla-pricing/configurations/${configurationId}/bands/${bandId}`);
}

export async function saveCells(
  configurationId: number,
  cells: SlaPricingCell[],
): Promise<{ data: SlaPricingCell[] }> {
  return apiPut(`/sla-pricing/configurations/${configurationId}/cells`, { cells });
}
